import React from 'react'
import { Alert, Box, Card, CardContent, Chip, Grid, Typography } from '@mui/material'
import { Title } from 'react-admin'

import { DwCostsSection } from './DwCostsSection'
import { DwMeasureActions } from './DwMeasureActions'
import { DwStatusTiles } from './DwStatusTiles'
import { useDwUsageStatus } from './useDwUsageStatus'

// Écran de pilotage de la mesure de consommation GCP.
//
// Il existe parce que la mesure est un cron silencieux qui alimente des écrans
// de facturation. Tant que rien ne la regardait, plusieurs nuits d'échec se
// lisaient comme une baisse de consommation chez les clients, et non comme une
// panne — on ne s'en apercevait qu'en cherchant autre chose.
//
// ⚠️ Le périmètre mesuré est TOUT le compte de facturation, pas les seuls
// datawarehouses clients : d'où le compteur de projets non rattachés, qui est la
// dépense que personne ne réclame.

// #region DatawarehouseScreen
export const DatawarehouseScreen: React.FC = () => {
	const { status, loading, reload } = useDwUsageStatus()

	return (
		<Box sx={{ p: 2 }}>
			<Title title="Datawarehouse" />
			<Typography variant="h5" gutterBottom>
				Datawarehouse — mesure de consommation
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Relevé quotidien de ce que Google facture sur l’ensemble du compte de facturation, au
				grain projet / jour / SKU. Alimente les écrans de dépassement et la consommation des
				comptes sous contrat.
			</Typography>

			{!loading && !status && (
				<Alert severity="error">État indisponible — l’API ne répond pas.</Alert>
			)}

			{status && (
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<DwStatusTiles status={status} />
					</Grid>

					<Grid item xs={12}>
						<DwCostsSection status={status} />
					</Grid>

					{/* ⚠️ Le signal le plus important de l'écran, et le seul qui ne se
					    devine pas : une journée sans aucune mesure ne se distingue pas
					    d'une journée sans consommation une fois qu'elle est passée. */}
					<Grid item xs={12}>
						<Card>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Journées manquantes
								</Typography>
								{status.missingDays.length === 0 ? (
									<Alert severity="success">
										Aucun trou entre le {status.firstUsageDate} et le {status.lastUsageDate}.
									</Alert>
								) : (
									<>
										<Alert severity="warning" sx={{ mb: 1.5 }}>
											{status.missingDays.length}
											{status.missingDaysTruncated ? '+' : ''} journée(s) sans aucune mesure.
											Ces jours comptent pour zéro dans tous les écrans de consommation — lance
											une reprise couvrant la plus ancienne.
										</Alert>
										<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
											{status.missingDays.map((d) => (
												<Chip key={d} label={d} size="small" color="warning" variant="outlined" />
											))}
											{status.missingDaysTruncated && (
												<Chip label="…" size="small" variant="outlined" />
											)}
										</Box>
									</>
								)}
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12}>
						<DwMeasureActions status={status} onDone={reload} />
					</Grid>
				</Grid>
			)}
		</Box>
	)
}

// #endregion
